import { useEffect, useRef, useState } from "react";
import { Button, Container, Tab, Row, Col } from "react-bootstrap";
import CommonLayout from "./commonLayout";
import {
  ParticipantsTable,
  TeamTable,
  Group,
  SessionModal,
} from "@/components";
import { PracticeRunAboutModel, PracticeRunsessionModal } from "@/components/modal";
import { useNavigate, useParams, Navigate } from "react-router-dom";
import { decrypt, encrypt } from "@/utils/crypto";
import { constant } from "@/utils/constant";
import { parseJwt } from "../utils/custom";
import SignOutPage from "@/components/signOutPage";
import { emitRandomAssignParticipants } from "@/utils/socketHelpers";

const Participants = ({ socket }) => {
  const params = useParams();
  const navigate = useNavigate();
  const [isRandomBtn, setIsRandomBtn] = useState(false);
  const [eventKey, setEventKey] = useState(
    params.dynamicPart || "participants"
  );
  const { id: session_id } = useParams();
  const [isAscending, setAscending] = useState(true);
  const [show, setShow] = useState(false);
  const [showPracticeRun, setShowPracticeRun] = useState(false);
  const [showPracticeRunSession, setShowPracticeRunSession] = useState(false);
  const [userData, setUserData] = useState(null);
  const [teamData, setTeamData] = useState([]);
  const [teamsLoading, setTeamsLoading] = useState(true);
  const [data, setData] = useState({});
  const [participantData, setParticipantData] = useState([]);
  const [participantLoading, setParticipantLoading] = useState(false);
  const [teamParticipants, setTeamParticipants] = useState([]);
  const [roleList, setRoleList] = useState([]);
  const [sessionDataLoading, setSessionDataLoading] = useState(true);
  const [userDataLoading, setUserDataLoading] = useState(true);

  const teamParticipantsRef = useRef(teamParticipants);
  const teamRef = useRef(teamData);

  /** Optimistic update: show assignment in UI immediately; sync with real data when res-team-participants fires or on error revert */
  const applyOptimisticReassign = (teamId, roleId, participantId, display = {}) => {
    setTeamData((prevTeams) =>
      (prevTeams || []).map((team) => {
        if (team.id !== teamId) return team;
        return {
          ...team,
          participant: (team.participant || []).map((p) => {
            if (p.role_id?.id !== roleId) return p;
            return {
              ...p,
              participant_id:
                participantId != null
                  ? {
                      id: participantId,
                      first_name: display.first_name ?? "",
                      last_name: display.last_name ?? "",
                    }
                  : null,
              // Clear is_game_leave so UI shows primary color (not red) for assigned slot
              ...(participantId != null && { is_game_leave: false }),
            };
          }),
        };
      })
    );
  };

  /** Optimistic unassign: clear role slot in UI immediately; sync when res-team-participants fires or on error revert */
  const applyOptimisticUnassign = (teamId, roleId) => {
    applyOptimisticReassign(teamId, roleId, null, {});
  };

  const fetchUserData = async (forceRefresh = false) => {
    try {
      const decodedJwt = parseJwt(localStorage.getItem("token"));
      if (!decodedJwt?.sub) {
        setUserDataLoading(false);
        return;
      }

      // Remove existing listener to prevent duplicates
      socket.off("fetch-single-user-data");

      socket.on("fetch-single-user-data", async (response) => {
        if (response.status === true) {
          // IDOR Protection: Set user credentials for authorization BEFORE setting userData
          if (response.data?.user?.uuid && response.data?.organization?.id) {
            await socket.setUserCredentials(
              response.data.user.uuid,
              response.data.organization.id
            );
          }
          setUserData(response.data);
        }
        setUserDataLoading(false);
        socket.off("fetch-single-user-data");
      });

      socket.emit("fetch-single-user-data", {
        user_id: decodedJwt.sub,
      });
    } catch (error) {
      setUserDataLoading(false);
    }
  };
  const fetchTeamData = async (showLoading = false) => {
    try {
      if (showLoading) {
        setTeamsLoading(true);
      }
      if (params?.id && socket) {
        // Remove existing listener to prevent duplicates
        socket.off("fetch-team-data");

        // Register listener BEFORE emitting (fix for race condition)
        socket.on("fetch-team-data", (response) => {
          if (response.status === true) {
            getGroupTeamGameData(response.data);
          }
          if (showLoading) {
            setTeamsLoading(false);
          }
          socket.off("fetch-team-data");
        });

        socket.emit("fetch-team-data", {
          session_id: Number(decrypt(params?.id)),
        });
      }
    } catch (error) {
      if (showLoading) {
        setTeamsLoading(false);
      }
    }
  };
  const fetchData = async (showLoading = false) => {
    try {
      if (params?.id) {
        if (showLoading) {
          setSessionDataLoading(true);
        }

        socket.off("fetch-single-session-data");

        // Register listener BEFORE emitting (fix for race condition)
        socket.on("fetch-single-session-data", (response) => {
          if (response.status === true && Number(response.data.session.id) === Number(decrypt(session_id))) {
            setData(response.data);
          }
          // Always set loading to false when data is received (fix for closure capture issue)
          setSessionDataLoading(false);
          socket.off("fetch-single-session-data");
        });

        socket.emit("fetch-single-session-data", {
          id: Number(decrypt(params?.id)),
        });
      }
    } catch (error) {
      if (showLoading) {
        setSessionDataLoading(false);
      }
    }
  };
  const fetchParticipants = async (showLoading = false) => {
    try {
      if (params?.id) {
        if (showLoading) {
          setParticipantLoading(true);
        }

        socket.off("fetch-participants");

        // Register listener BEFORE emitting (fix for race condition)
        socket.on("fetch-participants", (response) => {
          if (response.status === true) {
            setParticipantData(response.data || []);
          }
          if (showLoading) {
            setParticipantLoading(false);
          }
          socket.off("fetch-participants");
        });

        socket.emit("fetch-participants", {
          session_id: Number(decrypt(params?.id)),
          sort_key: "first_name",
          ascending: true,
        });
      }
    } catch (error) {
      if (showLoading) {
        setParticipantLoading(false);
      }
    }
  };
  const fetchTeamParticipants = async () => {
    try {
      if (params?.id) {
        // Remove existing listener to prevent duplicates
        socket.off("fetch-team-participant");

        // Register listener BEFORE emitting (fix for race condition)
        socket.on("fetch-team-participant", (response) => {
          if (response.status === true) {
            setTeamParticipants(response.data || []);
          }
          socket.off("fetch-team-participant");
        });

        socket.emit("fetch-team-participant", {
          session_id: Number(decrypt(params?.id)),
        });
      }
    } catch (error) {
      console.error("Error fetching team participants:", error);
    }
  };

  useEffect(() => {
    fetchUserData();
    fetchTeamData(true);
    fetchData(true);
    fetchParticipants(true);
    fetchTeamParticipants();
    const fetchRoleData = async () => {
      socket.off("fetch-role-list");
      socket.emit("fetch-role-list");
      socket.on("fetch-role-list", (response) => {
        if (response.status === true) {
          setRoleList(response.data || { roles: [] });
        }
        socket.off("fetch-role-list");
      });
    };
    fetchRoleData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSocket = (sessionId) => {
    if (data.session.is_game_complete === 0) {
      socket.emit("joinSessionRoom", sessionId);
      socket.on("resJoinSessionRoom", () => {
        socket.emit("register-session-subscribe", {
          session_id: sessionId,
          isStartGameCount: data.session.no_of_participant,
        });
      });
    }
  };

  useEffect(() => {
    if (!data.session) return;
    const sessionId = Number(decrypt(session_id));
    handleSocket(sessionId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.session]);

  useEffect(() => {
    if (!data.session) return;
    const sessionId = Number(decrypt(session_id));
    const handleConnectionEstablished = () => {
      if (["participants", "teams", "group"].includes(params.dynamicPart)) {
        handleSocket(sessionId);
      }
    };
    socket.on("connectionEstablished", handleConnectionEstablished);
    return () => {
      socket.off("connectionEstablished", handleConnectionEstablished);
      socket.off("resJoinSessionRoom");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.session]);

  useEffect(() => {
    teamParticipantsRef.current = teamParticipants;
    teamRef.current = teamData;
  }, [teamParticipants, teamData]);

  // Calculate unassign participant.
  const unassigned =
    participantData &&
    participantData?.length !== 0 &&
    !data?.session?.is_open_game_link;

  const handleTabSelect = (selectedEventKey) => {
    navigate(`/${selectedEventKey}/${encrypt(String(decrypt(params?.id)))}`);
    setEventKey(selectedEventKey);
  };

  useEffect(() => {
    if (!socket) return;

    const sessionId = Number(decrypt(session_id));
    let hasFetched = false; // guard to prevent multiple fetch calls

    const handleCheck = async () => {
      socket.emit("isInRoom", `${sessionId}`, async (isInRoom) => {
        if (!isInRoom && !hasFetched) {
          socket.emit("joinSessionRoom", sessionId);
          await fetchData();
          hasFetched = true;
          // reset after a short delay so future disconnects can trigger fetchData again
          setTimeout(() => {
            hasFetched = false;
          }, 3000);
        }
      });
    };
    // Run immediately on mount
    handleCheck();

    // Run every 5s
    const intervalRoom = setInterval(handleCheck, 5000);

    // Also run once on reconnect
    socket.on("connect", () => {
      hasFetched = false; // reset guard
      handleCheck();
    });

    return () => {
      clearInterval(intervalRoom);
      socket.off("connect");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket]);

  const getGroupTeamGameData = async (teamDetails) => {
    try {
      const data = teamDetails || [];

      if (data.length > 0) {
        const teamIds = data.map((item) => item?.id).filter(Boolean);
        if (teamIds.length === 0) {
          return;
        }
        // Remove existing listener to prevent duplicates
        socket.off("res-fetch-games-data-by-teams");
        socket.on("res-fetch-games-data-by-teams", (response) => {
          if (response.status === true) {
            const games = response.data || [];
            const gamesByTeam = {};
            games.forEach((game) => {
              if (!gamesByTeam[game.team_id]) {
                gamesByTeam[game.team_id] = [];
              }
              gamesByTeam[game.team_id].push(game);
            });

            // Merge games data with existing team data. When team count changed (e.g. after
            // participant count update), use response as source of truth so UI updates without refresh.
            setTeamData((prevTeams) => {
              const teamCountChanged = prevTeams.length > 0 && data.length !== prevTeams.length;
              if (prevTeams.length === 0 || teamCountChanged) {
                return data.map((team) => ({
                  ...team,
                  games: gamesByTeam[team?.id || team?.team_id] || [],
                }));
              }
              return prevTeams.map((team) => {
                const teamId = team?.team_id || team?.id;
                const updatedTeam = data.find((t) => (t?.team_id || t?.id) === teamId);
                return {
                  ...team,
                  ...(updatedTeam || {}),
                  games: gamesByTeam[teamId] || [],
                };
              });
            });
            setTeamsLoading(false);
          } else {
            setTeamsLoading(false);
          }
          socket.off("res-fetch-games-data-by-teams");
        });
        
        socket.emit("fetch-games-data-by-teams", {
          session_id: Number(decrypt(session_id)),
          team_ids: teamIds,
        });
      }
    } catch (error) {
      setTeamsLoading(false);
    }
  };

  useEffect(() => {
    if (
      data.session?.no_of_participant &&
      data.session.is_game_complete === 0
    ) {
      socket.emit("register-session-subscribe", {
        session_id: Number(decrypt(session_id)),
        isStartGameCount: data.session?.no_of_participant,
      });
    }
    socket.on("res-register-session-subscribe", (response) => {
      setTeamData((prevTeams) => {
        return (
          prevTeams?.map((item) => {
            const updatedData = response.teams.find(
              (team_item) => team_item.id === item.id
            );
            if (updatedData) {
              return {
                ...item,
                ...updatedData,
              };
            } else {
              return item;
            }
          }) || []
        );
      });
      if (response?.participant?.length > 0) {
        setParticipantData(response?.participant);
        fetchTeamParticipants();
      }
    });
    socket.on("res-team-participants", (response) => {
      // Check if any team's round changed before updating state
      let hasRoundChange = false;
      if (response.team && response.team.length > 0) {
        const latestTeams = teamRef.current ?? [];
        hasRoundChange = response.team.some((updatedTeam) => {
          const prevTeam = latestTeams.find((t) => t.id === updatedTeam.id);
          return prevTeam != null &&
                 updatedTeam.current_round !== undefined &&
                 prevTeam.current_round !== updatedTeam.current_round;
        });
      }

      setTeamData((prevTeams) => {
        return (
          prevTeams?.map((team) => {
            // Filter participants for this team only
            const teamParticipantsForThisTeam =
              response.teamParticipants.filter(
                (item) => item.team_id === team.id
              );
            const updatedData =
              teamParticipantsForThisTeam.length > 0
                ? teamParticipantsForThisTeam[0]
                : null;
            const updatedParticipant = teamParticipantsForThisTeam.map(
              (item) => {
                const participant = team?.participant?.find(
                  (p) => Number(p.id) === Number(item.id)
                );
                const normalizedRoleId = item.role
                  ? { id: item.role.id, name: item.role.name }
                  : { id: item.role_id };
                if (participant) {
                  const roleId =
                    participant.role_id &&
                    typeof participant.role_id === "object"
                      ? participant.role_id
                      : {
                          id:
                            participant.role_id ??
                            item.role_id ??
                            item.role?.id,
                          name: item.role?.name,
                        };
                  return {
                    ...participant,
                    role_id: roleId,
                    participant_id: item.participant ?? null,
                    current_status: item.current_status,
                    is_game_leave: item.is_game_leave,
                  };
                }
                return {
                  ...item,
                  role_id: normalizedRoleId,
                  participant_id: item.participant ?? null,
                  current_status: item.current_status,
                  is_game_leave: item.is_game_leave,
                };
              }
            );
            if (updatedData) {
              const teamStatus = response.team?.find(
                (item) => item.id === team.id
              )?.current_status;
              return {
                ...team,
                current_status: teamStatus,
                team_participant: updatedData.team_participant,
                participant: updatedParticipant,
              };
            }
            return team;
          }) || []
        );
      });

      // Update teamParticipants directly from socket response (real-time)
      if (response.teamParticipants) {
        setTeamParticipants(response.teamParticipants);
      }

      // Fetch updated data in background without showing loading spinners
      fetchParticipants(false);
      fetchData(false);
      // If round changed, also refresh games data for accurate activity display
      if (hasRoundChange && response.team) {
        getGroupTeamGameData(response.team);
      }
    });
    // Got latest game data when move to next round
    socket.on("res-session-game-data", (response) => {
      const participants = response.teamParticipants;

      const formattedParticipants = participants.map((p) => ({
        id: p.id,
        role_id: p.role,
        session_id: p.session_id,
        is_game_leave: p.is_game_leave,
        current_status: p.current_status,
        participant_id: p.participant,
        is_help_call_for_facilitator: p.is_help_call_for_facilitator,
      }));

      const updatedTeamData = response.team.map((t) => ({
        ...t,
        participant: formattedParticipants,
      }));

      getGroupTeamGameData(updatedTeamData);
    });
    // when i change the team data
    socket.on("res-update-team-record", (response) => {
      setTeamData((prevTeams) => {
        return (
          prevTeams?.map((team) => {
            const updatedTeam = response.data.find((t) => t.id === team.id);
            if (updatedTeam) {
              return { ...team, ...updatedTeam };
            }
            return team;
          }) || []
        );
      });
    });
    socket.on("res-get-session-details", (response) => {
      if (response.success === true && response.data && Number(response.data.id) === Number(decrypt(session_id))) {
        setData((prevState) => ({
          ...prevState,
          session: response.data,
        }));
      }
    });
    socket.on("update-session-data", (response) => {
      if (response.status === true && Number(response.data.session.id) === Number(decrypt(session_id))) {
        setData((prevState) => ({
          ...prevState,
          session: response.data.session,
        }));
        // when session data is updated, fetch the team and participant data
        // Don't show loading spinners for background updates
        fetchTeamData();
        fetchParticipants();
        // Fetch updated user data in the background without clearing existing data
        fetchUserData(true);
      }
    });
    socket.on("updatepractice-run-session-data", (response) => {
      if (response.status === true && Number(response.data.session.id) === Number(decrypt(session_id))) {
        setData((prevState) => ({
          ...prevState,
          session: response.data.session,
        }));
        fetchTeamData();
        fetchParticipants();
        fetchUserData(true);
      }
    });
    socket.on("game-completed", async (response) => {
      if(Number(response.session_id) === Number(decrypt(session_id))) {
        await fetchTeamData();
      }
    });
    return () => {
      socket.off("res-register-session-subscribe");
      socket.off("res-team-participants");
      socket.off("res-session-game-data");
      socket.off("res-get-session-details");
      socket.off("update-session-data");
      socket.off("updatepractice-run-session-data");
      socket.off("game-completed");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.session?.no_of_participant]);

  const assignRandomParticipant = async () => {
    try {
      setIsRandomBtn(true);
      const session_id = Number(decrypt(params?.id));
      const response = await emitRandomAssignParticipants(socket, {
        session_id: session_id,
      });
      if (response?.status) {
        setIsRandomBtn(false);
      } else {
        setIsRandomBtn(false);
      }
    } catch (error) {
      setIsRandomBtn(false);
    }
  };

  const handleAscending = async (isBool) => {
    setAscending(isBool);
    navigate("/session", {
      state: {
        is_ascending: isBool,
      },
    });
  };

  const handleShow = () => {
    setShow(!show);
  };

  const handleShowPracticeRun = () => {
    setShowPracticeRun((prev) => !prev);
  };

  if (!["participants", "teams", "group"].includes(params.dynamicPart)) {
    return <Navigate to="/session" replace />;
  }

  // Determine loading and authorization states to prevent white screen
  const isLoading = sessionDataLoading || userDataLoading;
  const hasRequiredData =
    userData?.organization?.id !== undefined &&
    data?.session?.organization_id !== undefined;
  const isAuthorized =
    hasRequiredData &&
    Number(userData?.organization?.id) ===
      Number(data?.session?.organization_id);

  return (
    <>
      <Tab.Container id="tab" activeKey={eventKey} onSelect={handleTabSelect}>
        {isLoading ? (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ minHeight: "50vh" }}
          >
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : isAuthorized ? (
          <>
            <CommonLayout
              isAscending={isAscending}
              handleAscending={handleAscending}
              handleShow={handleShow}
              handleShowPracticeRun={handleShowPracticeRun}
              userData={userData}
              socket={socket}
              participants={participantData}
              participantsLoading={participantLoading}
              teamData={teamData}
              teamsLoading={teamsLoading}
              sessionData={data}
              teamParticipants={teamParticipants}
              activeKey={eventKey}
            >
              <Container className="mt-10">
                <Tab.Content>
                  <Tab.Pane eventKey="participants">
                    {unassigned && (
                      <Button
                        variant="outline-primary"
                        className="mb-8"
                        disabled={participantData?.length === 0 || isRandomBtn}
                        onClick={() => assignRandomParticipant()}
                      >
                        {constant?.randomizeAssignments}
                      </Button>
                    )}
                    <ParticipantsTable
                      setEventKey={setEventKey}
                      sessionData={data}
                      participantData={participantData}
                      teamData={teamData}
                      participantLoading={participantLoading}
                      socket={socket}
                    />
                  </Tab.Pane>
                  <Tab.Pane eventKey="teams">
                    <Row className="gy-20 mb-10">
                      {params.dynamicPart === "teams" &&
                        eventKey === "teams" &&
                        (teamData?.length ? (
                          teamData?.map((item, index) => {
                            return (
                              <Col lg={6} key={index}>
                                <TeamTable
                                  teamParticipants={teamParticipants}
                                  allTeamData={teamData}
                                  teamData={item}
                                  sessionData={data}
                                  participantData={participantData}
                                  participantLoading={participantLoading}
                                  socket={socket}
                                  roleList={roleList}
                                  onOptimisticReassign={applyOptimisticReassign}
                                  onOptimisticUnassign={applyOptimisticUnassign}
                                />
                              </Col>
                            );
                          })
                        ) : (
                          <Col lg={6}>
                            <TeamTable />
                          </Col>
                        ))}
                    </Row>
                  </Tab.Pane>
                  <Tab.Pane eventKey="group">
                    <Group
                      teamData={teamData}
                      sessionData={data}
                      socket={socket}
                    />
                  </Tab.Pane>
                </Tab.Content>
              </Container>
            </CommonLayout>
            {show && (
              <SessionModal
                show={show}
                hide={() => handleShow()}
                handleAscending={handleAscending}
                isAscending={isAscending}
                userData={userData}
                socket={socket}
              />
            )}
            {showPracticeRun && (
              <PracticeRunAboutModel
                show={showPracticeRun}
                hide={handleShowPracticeRun}
                openPracticeRunSessionModel={() => {
                  setShowPracticeRunSession(true);
                  handleShowPracticeRun();
                }}
              />
            )}
            {showPracticeRunSession && (
              <PracticeRunsessionModal
                show={showPracticeRunSession}
                hide={() => setShowPracticeRunSession(false)}
                handleAscending={handleAscending}
                isAscending={isAscending}
                userData={userData}
                socket={socket}
              />
            )}
          </>
        ) : (
          hasRequiredData && <SignOutPage session_id={params.id} />
        )}
      </Tab.Container>
    </>
  );
};
export default Participants;
